import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FiValidatingFormDirective } from '@fi-shared/fi-directives/fi-validating-form/fi-validating-form.directive';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import {
    ICheckpointAction,
    ICheckpointActionNotificationSettingsDialogData,
    ICheckpointActionAlertRecipient,
} from '@fi-shared/checklist/checklist-v2';
import { cloneDeep } from '@fi-shared/utils';
import { CheckpointActionConditionType, CheckpointActionRecipientType } from '@fi-shared/checklist/checklist-enum';
import { AppTextService } from '@fi-shared/app-text';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { Subject } from 'rxjs';

@Component({
    selector: 'fi-checkpoint-action-notification-editor-dialog',
    templateUrl: './action-notification-editor-dialog.component.html',
    styleUrls: ['./action-notification-editor-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-action-notification-editor-dialog' },
    standalone: false,
})
export class FiCheckpointActionNotificationEditorDialogComponent
    extends FiDialogsContentBase<ICheckpointActionNotificationSettingsDialogData, ICheckpointAction>
    implements OnInit, OnDestroy
{
    @ViewChild('sendNotificationEditorForm') sendNotificationEditorForm: FiValidatingFormDirective;

    protected action: ICheckpointAction = cloneDeep(this.data.action);
    private appTextService: AppTextService;
    private fiDialogsService: FiDialogsService;
    private fiValuesComparison: FiValuesComparison<ICheckpointAction>;

    public ngOnInit(): void {
        this.setServices();
        this.fiValuesComparison = new FiValuesComparison(() => this.action);
        this.subscribeOnTransitionCloseDialog(this.fiDialogsService, this.ngOnDestroy$);
    }

    public isYesButtonDisabled(): boolean {
        return !!this.sendNotificationEditorForm.errors || this.sendNotificationEditorForm.pending;
    }

    protected updateValidity(): void {
        this.sendNotificationEditorForm.updateValidity();
    }

    protected onRecipientChange(recipient: ICheckpointActionAlertRecipient): void {
        this.action.Settings.AlertRecipient = recipient;
        this.updateValidity();
    }

    protected isAlertTypeNotSelected(): boolean {
        return (
            !this.action.Settings.AlertRecipient.ViaSMS &&
            !this.action.Settings.AlertRecipient.ViaEmail &&
            !this.action.Settings.AlertRecipient.ViaNotification
        );
    }

    protected isNotificationMethodHidden(): boolean {
        return (
            this.isSMSRecipientType() ||
            this.action.Settings.AlertRecipient.RecipientType === CheckpointActionRecipientType.EmailAddress
        );
    }

    protected isComplianceThresholdMode(): boolean {
        return this.action.ActionCondition === CheckpointActionConditionType.ComplianceThreshold;
    }

    private isSMSRecipientType(): boolean {
        return this.action.Settings.AlertRecipient.RecipientType === CheckpointActionRecipientType.SMS;
    }

    protected getMethodDescriptions(): string {
        return this.appTextService.get(
            !this.isComplianceThresholdMode()
                ? 'checklist.checkpoint_notification_methods_description'
                : 'checklist.compliance_notification_methods_description',
        );
    }

    protected getActionNotificationSendMessage(): string {
        return this.appTextService.get(
            !this.data.complianceThresholdMode
                ? 'checklist.checkpoint_action_notification_dialog_message'
                : 'checklist.compliance_threshold_action_notification_dialog_message',
        );
    }

    public getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    private setServices(): void {
        this.appTextService = this.injector.get(AppTextService);
        this.fiDialogsService = this.injector.get(FiDialogsService);
    }

    public getResult(): ICheckpointAction {
        return this.action;
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
